import {Component, Inject, Input, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {TranslocoService} from "@ngneat/transloco";

type IHotKeyMode = 'scene' | 'slide' | 'painter' | 'timeline' | 'scheduler';
interface IHotKey {
    key: string;
    description: string;
    hidden?: boolean;
}

interface IHotKeys {
    group: string;
    description?: string;
    keys: Array<IHotKey>;
}

interface IData {
    mode: IHotKeyMode
}

@Component({
    selector: 'app-dialog-hotkeys',
    templateUrl: './dialog-hotkeys.component.html',
    styleUrls: ['./dialog-hotkeys.component.scss']
})
export class DialogHotkeysComponent implements OnInit {

    @Input() mode: 'slideMode' | 'sceneMode' = 'sceneMode';
    hotkeys: Array<IHotKeys> = [];
    hotkeyMode: IHotKeyMode = 'scene';

    constructor(private translocoService: TranslocoService, @Inject(MAT_DIALOG_DATA) public data: IData) {
        if (data && data.mode) {
            this.hotkeyMode = data.mode;
        } else {
            // Set default based on input mode
            this.hotkeyMode = this.mode === 'slideMode' ? 'slide' : 'scene';
        }
    }

    ngOnInit(): void {
        switch(this.hotkeyMode) {
            case 'slide': {
                const hidden = false;
                this.hotkeys = [
                    {
                        group: this.translate('general.general'),
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + C', description: this.translate('general.copy')},
                            {key: 'Ctrl + X', description: this.translate('general.cut')},
                            {key: 'Ctrl + V', description: this.translate('general.paste')},

                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},

                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},

                            {key: 'Ctrl + L', description: this.translate('general.toggleGuide'), hidden},
                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')},
                            {key: 'Esc', description: this.translate('general.selectScene')}
                        ]
                    },
                    {
                        group: this.translate('general.items'),
                        description: 'selected items hotkeys',
                        keys: [
                            {key: 'Ctrl, Shift + Mouse', description: this.translate('general.addToSelection2'), hidden},
                            {key: 'Delete', description: this.translate('general.removeItems')},
                            {key: 'Alt + T', description: this.translate('general.addLabel'), hidden},
                            {key: 'Alt + P', description: this.translate('general.goBackPreviousScene'), hidden},

                            {key: 'Alt + Up', description: this.translate('general.pushUp')},
                            {key: 'Alt + Down', description: this.translate('general.pushDown')},
                            {key: 'Alt + Shift + Up', description: this.translate('general.pushTop')},
                            {key: 'Alt + Shift + Down', description: this.translate('general.pushBottom')},

                            {key: 'Alt + LeftRight', description: this.translate('general.rotate') + ' (1&#176;)'},
                            {key: 'Alt + Shift + LeftRight', description: this.translate('general.rotate') + ' (45&#176;)'},
                            {key: 'Arrow', description: this.translate('scenes.move')},
                            {key: 'Shift + Arrow', description: this.translate('scenes.move') + ' x 10(px)'},
                            {key: 'Ctrl + Arrow', description: this.translate('scenes.resize'), hidden},
                            {key: 'Ctrl + Shift + Arrow', description: this.translate('scenes.resize') + ' x 10(px)', hidden},

                            {key: 'Ctrl + O', description: this.translate('general.orientVertical'), hidden},
                            {key: 'Ctrl + Shift + O', description: this.translate('general.orientHorizontal'), hidden},
                            {key: 'Ctrl + Shift + S', description: this.translate('general.showAll'), hidden},
                            {key: 'Ctrl + Shift + H', description: this.translate('general.hideItem'), hidden},
                        ]
                    },
                    {
                        group: 'Presentation Navigation',
                        description: 'powerpoint style navigation',
                        keys: [
                            {key: 'Right', description: 'Next slide or next animation'},
                            {key: 'Down', description: 'Next slide or next animation'},
                            {key: 'Left', description: 'Previous slide or previous animation'},
                            {key: 'Up', description: 'Previous slide or previous animation'},
                            {key: 'Spacebar', description: 'Next slide or next animation'},
                            {key: 'Backspace', description: 'Previous slide'},
                            {key: 'Page Down', description: 'Next slide'},
                            {key: 'Page Up', description: 'Previous slide'},
                            {key: 'Enter', description: 'Next slide'},
                            {key: 'Home', description: 'Jump to first slide'},
                            {key: 'End', description: 'Jump to last slide'},
                            {key: 'B', description: 'Black screen (press again to return)'},
                            {key: 'W', description: 'White screen (press again to return)'},
                            {key: 'Esc', description: 'End the slideshow'}
                        ]
                    }
                ];
                break;
            }
            case 'scene':
            case 'painter': {
                const hidden = this.hotkeyMode === 'painter';
                this.hotkeys = [
                    {
                        group: this.translate('general.general'),
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + C', description: this.translate('general.copy')},
                            {key: 'Ctrl + X', description: this.translate('general.cut')},
                            {key: 'Ctrl + V', description: this.translate('general.paste')},
        
                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},
        
                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},
        
                            {key: 'Ctrl + L', description: this.translate('general.toggleGuide'), hidden},
                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')},
                            {key: 'Esc', description: this.translate('general.selectScene')}
                        ]
                    },
                    {
                        group: this.translate('general.items'),
                        description: 'selected items hotkeys',
                        keys: [
                            {key: 'Ctrl, Shift + Mouse', description: this.translate('general.addToSelection2'), hidden},
                            {key: 'Delete', description: this.translate('general.removeItems')},
                            {key: 'Alt + T', description: this.translate('general.addLabel'), hidden},
                            {key: 'Alt + P', description: this.translate('general.goBackPreviousScene'), hidden},
        
                            {key: 'Alt + Up', description: this.translate('general.pushUp')},
                            {key: 'Alt + Down', description: this.translate('general.pushDown')},
                            {key: 'Alt + Shift + Up', description: this.translate('general.pushTop')},
                            {key: 'Alt + Shift + Down', description: this.translate('general.pushBottom')},
        
                            {key: 'Alt + LeftRight', description: this.translate('general.rotate') + ' (1&#176;)'},
                            {key: 'Alt + Shift + LeftRight', description: this.translate('general.rotate') + ' (45&#176;)'},
                            {key: 'Arrow', description: this.translate('scenes.move')},
                            {key: 'Shift + Arrow', description: this.translate('scenes.move') + ' x 10(px)'},
                            {key: 'Ctrl + Arrow', description: this.translate('scenes.resize'), hidden},
                            {key: 'Ctrl + Shift + Arrow', description: this.translate('scenes.resize') + ' x 10(px)', hidden},
        
                            {key: 'Ctrl + O', description: this.translate('general.orientVertical'), hidden},
                            {key: 'Ctrl + Shift + O', description: this.translate('general.orientHorizontal'), hidden},
                            {key: 'Ctrl + Shift + S', description: this.translate('general.showAll'), hidden},
                            {key: 'Ctrl + Shift + H', description: this.translate('general.hideItem'), hidden},
                        ]
                    },
                ];
                break;
            }
            case 'timeline':
            case 'scheduler': {
                this.hotkeys = [
                    {
                        group: '',
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + V', description: this.translate('general.duplicate')},        
                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},
                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},
                            {key: 'Alt + S', description: this.translate('general.toggleSpecialDay')},
                            {key: 'Ctrl + L', description: this.translate('general.lowPriority')},
                            {key: 'Ctrl + M', description: this.translate('general.mediumPriority')},
                            {key: 'Ctrl + H', description: this.translate('general.highPriority')},

                            // {key: 'Alt + O', description: `${this.translate('general.repeatType')}: ${this.translate('general.once')} (incoming...)`},
                            // {key: 'Alt + D', description: `${this.translate('general.repeatType')}: ${this.translate('general.daily')} (incoming...)`},
                            // {key: 'Alt + W', description: `${this.translate('general.repeatType')}: ${this.translate('general.weekly')} (incoming...)`},
                            // {key: 'Alt + M', description: `${this.translate('general.repeatType')}: ${this.translate('general.monthly')} (incoming...)`},
                            // {key: 'Alt + Y', description: `${this.translate('general.repeatType')}: ${this.translate('general.yearly')} (incoming...)`},

                            // {key: 'Alt + Arrow', description: 'select (incoming...)'},
                            // {key: 'Shift + Arrow', description: 'resize (incoming...)'},
                            // {key: 'Ctrl + Arrow', description: 'drag (incoming...)'},

                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')}
                        ]
                    }
                ];
                break;
            }
        }
        this.parseHotkeys();
    }

    private translate(terms) {
        return this.translocoService.translate(terms, {}, this.translocoService.getActiveLang());
    }

    private parseHotkeys() {
        let isMac = false;
        if (navigator['userAgentData']) {
            isMac = navigator['userAgentData']?.platform?.includes('Mac');
        }

        const symbols = {
            Ctrl: isMac ? '&#8984;' : 'Ctrl', // ⌘
            Left: '&#129092;', // ←
            Right: '&#129094;', // →
            Up: '&#129093;', // ↑
            Down: '&#129095;', // ↓
            Arrow: '&#10016;',
            LeftRight: '&#8646;',
            UpDown: '&#8645;',
            Plus: '&#10010;',
            Minus: '&#9472;'
        };

        for (const group of this.hotkeys) {
            for (const item of group['keys']) {
                Object.keys(symbols).forEach(symbol => {
                    item['key'] = item['key'].replace(new RegExp("\\b" + symbol + "\\b", "ig"), symbols[symbol]);
                });
            }
        }
    }
}
